import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { selectUser } from '@/store/selectors/userSelectors';
import { useSelector } from 'react-redux';
import { isEmpty } from '@/shared/utility';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { BffUser } from '@lucky7ventures/bff-types';
import { useModal } from '@/features/modal/useModal';
import { MODALS } from '@/features/modal/types';

export function useStateUpdateToast(
  addToastCallback: (message: any) => void,
  cleanupToastCallback: (key: string) => void,
): void {
  const user = useSelector(selectUser);
  const TOAST_ID = 'STATE_UPDATE_TOAST';
  const intl = useIntl();
  const { openModal } = useModal();

  const handleCtaClick = () => {
    openModal(MODALS.STATE_UPDATE);
  };

  const shouldPresentToast = (user: BffUser | null): boolean => {
    if (isEmpty(user)) {
      return false;
    }

    // we want to show this toast only to canada users
    if (user?.countryCode !== 'CA') {
      return false;
    }

    // ofc GiG is weird about it and they dont put null, rather they put this subdivision code for
    // a user that doesn't have subdivision set and also the subdivisionId is set to
    // '00000000-0000-0000-0000-000000000000' (but we don't have subdivisionId in the user response)
    return user.subdivision.code === 'NA';
  };

  useEffect(() => {
    if (!shouldPresentToast(user)) {
      cleanupToastCallback(TOAST_ID);
      return;
    }

    addToastCallback({
      id: TOAST_ID,
      order: 1,
      title: intl.formatMessage({ id: 'state.update.notification.header' }),
      subtitle: intl.formatMessage({ id: 'state.update.notification.text' }),
      type: 'cta',
      cta: intl.formatMessage({ id: 'misc.update' }),
      icon: <ExclamationTriangleIcon className="h-6 w-6" />,
      clickCallback: handleCtaClick,
    });
  }, [user]);
}
